import React, { useState, useEffect, useCallback, useRef } from "react";
import API_BASE_URL from "../config";

const ITEMS_API = `${API_BASE_URL}api/Marketing_items`;

const generateNumber = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const getInitialForm = () => ({
  number: generateNumber(),
  series: "Primary",
  priceList: "Last Evaluated Price",
  postingDate: new Date().toISOString().split("T")[0],
  documentDate: new Date().toISOString().split("T")[0],
  ref2: "",
  remarks: "",
  journalRemark: "Goods Receipt",
  items: [
    { itemNo: "", itemDescription: "", quantity: "", accountCode: "101050500", unitPrice: "", whse: "", expenseCategory: "", addtlInfo: "" },
  ],
});

// ─── Search Icon ──────────────────────────────────────────────────────────────
const SearchIcon = ({ size = 14, color = "currentColor" }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// ─── Item Lookup Modal ────────────────────────────────────────────────────────
function ItemLookupModal({ open, onClose, onSelect, allItems, loading }) {
  const [search, setSearch] = useState("");

  const filtered = allItems.filter(i =>
    i.itemCode?.toLowerCase().includes(search.toLowerCase()) ||
    i.description?.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => { if (open) setSearch(""); }, [open]);

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(15,23,42,.5)", backdropFilter: "blur(5px)" }} onClick={onClose} />
      <div style={{
        position: "relative", zIndex: 10, background: "#fff", borderRadius: "18px",
        width: "min(640px, 94vw)", maxHeight: "80vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,.22)", overflow: "hidden",
        fontFamily: '"Outfit", sans-serif',
      }}>
        <div style={{ background: "linear-gradient(135deg,#185FA5,#0C447C)", padding: "1rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".625rem" }}>
            <SearchIcon size={16} color="#bfdbfe" />
            <span style={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Item Lookup</span>
            <span style={{ background: "rgba(255,255,255,.18)", color: "#bfdbfe", fontSize: ".72rem", fontWeight: 600, padding: ".2rem .7rem", borderRadius: "20px", border: "1px solid rgba(255,255,255,.2)" }}>
              Marketing Items
            </span>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: "7px", padding: ".35rem", cursor: "pointer", color: "#fff", display: "flex" }}>
            <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div style={{ padding: ".75rem 1rem", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}>
              <SearchIcon size={14} />
            </span>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search item code or description…"
              style={{ width: "100%", paddingLeft: 34, paddingRight: 12, height: 36, border: "1px solid #e2e8f0", borderRadius: 8, fontSize: ".875rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
            />
          </div>
          <p style={{ fontSize: ".72rem", color: "#94a3b8", marginTop: 6, marginLeft: 2 }}>
            {filtered.length} item{filtered.length !== 1 ? "s" : ""} — click a row to select
          </p>
        </div>
        <div style={{ overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "3rem", gap: 10, color: "#94a3b8" }}>
              <svg style={{ animation: "gr-spin .7s linear infinite", width: 20, height: 20 }} fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
              </svg>
              <span style={{ fontSize: ".875rem" }}>Loading items…</span>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".85rem" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0", position: "sticky", top: 0 }}>
                  {["Item Code", "Description", "Avail. Qty", "Unit"].map(h => (
                    <th key={h} style={{ padding: ".625rem 1rem", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: ".72rem", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: "3rem", textAlign: "center", color: "#94a3b8", fontSize: ".875rem" }}>
                      No items match your search.
                    </td>
                  </tr>
                ) : filtered.map(item => (
                  <tr
                    key={item.id}
                    onClick={() => onSelect(item)}
                    style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer", transition: "background .1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <td style={{ padding: ".625rem 1rem", fontFamily: "monospace", fontWeight: 700, color: "#185FA5", fontSize: ".82rem" }}>
                      {item.itemCode}
                    </td>
                    <td style={{ padding: ".625rem 1rem", color: "#334155", maxWidth: 220 }}>
                      {item.description || <span style={{ color: "#cbd5e1" }}>—</span>}
                    </td>
                    <td style={{ padding: ".625rem 1rem", fontWeight: 700, color: item.availableQty > 0 ? "#1D9E75" : "#E24B4A", fontSize: ".82rem" }}>
                      {Number(item.availableQty || 0).toLocaleString()}
                    </td>
                    <td style={{ padding: ".625rem 1rem" }}>
                      {item.unit
                        ? <span style={{ background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: 5, fontSize: ".75rem", fontFamily: "monospace", fontWeight: 600, border: "1px solid #e2e8f0" }}>{item.unit}</span>
                        : <span style={{ color: "#cbd5e1" }}>—</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div style={{ borderTop: "1px solid #f1f5f9", padding: ".75rem 1rem", background: "#f8fafc", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: ".4rem 1rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".82rem", fontWeight: 600, color: "#475569", background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline Typeahead Input ────────────────────────────────────────────────────
function TypeaheadInput({ value, onChange, onSelect, onOpenModal, allItems, field, placeholder, style }) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = value.trim().length === 0
    ? allItems.slice(0, 40)
    : allItems.filter(i =>
      i.isActive === 1 && (
        i.itemCode?.toLowerCase().includes(value.toLowerCase()) ||
        i.description?.toLowerCase().includes(value.toLowerCase())
      )
    ).slice(0, 40);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setCursor(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); setCursor(0); e.preventDefault(); }
      return;
    }
    if (e.key === "ArrowDown") { setCursor(c => Math.min(c + 1, filtered.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setCursor(c => Math.max(c - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter" && cursor >= 0 && filtered[cursor]) {
      onSelect(filtered[cursor]);
      setOpen(false);
      setCursor(-1);
      e.preventDefault();
    } else if (e.key === "Escape") { setOpen(false); setCursor(-1); }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setCursor(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="gr-input"
        style={{ ...style, paddingRight: "1.75rem" }}
        autoComplete="off"
      />
      <button
        type="button"
        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setOpen(false); onOpenModal && onOpenModal(); }}
        title="Browse items"
        style={{ position: "absolute", right: ".4rem", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 0 }}
        onMouseEnter={e => e.currentTarget.style.color = "#185FA5"}
        onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}
      >
        <SearchIcon size={13} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          width: field === "itemDescription" ? 320 : 280,
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          zIndex: 9999,
          boxShadow: "0 8px 32px rgba(0,0,0,.14)",
          maxHeight: 240,
          overflowY: "auto",
          fontSize: ".82rem",
        }}>
          <div style={{ padding: ".3rem .75rem", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", borderRadius: "10px 10px 0 0", fontSize: ".7rem", color: "#94a3b8", fontWeight: 600 }}>
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} — ↑↓ navigate · Enter select
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: ".75rem", textAlign: "center", color: "#94a3b8" }}>No items found</div>
          ) : filtered.map((item, i) => (
            <div
              key={item.id || item.itemCode}
              onMouseDown={() => { onSelect(item); setOpen(false); setCursor(-1); }}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: ".45rem .75rem",
                borderBottom: "1px solid #f8fafc",
                background: cursor === i ? "#eff6ff" : "transparent",
                cursor: "pointer",
                transition: "background .1s",
              }}
              onMouseEnter={() => setCursor(i)}
            >
              <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#185FA5", minWidth: 70, fontSize: ".78rem" }}>
                {item.itemCode}
              </span>
              <span style={{ color: "#334155", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.description || "—"}
              </span>
              <span style={{
                fontSize: ".72rem", fontWeight: 700,
                color: (item.availableQty || 0) > 0 ? "#1D9E75" : "#E24B4A",
                whiteSpace: "nowrap",
              }}>
                {Number(item.availableQty || 0).toLocaleString()}
              </span>
              {item.unit && (
                <span style={{ background: "#f1f5f9", color: "#475569", padding: "1px 6px", borderRadius: 4, fontSize: ".7rem", fontFamily: "monospace", border: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>
                  {item.unit}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const WAREHOUSES = [
  { code: "MSW-001", name: "1st Floor Warehouse" },
  { code: "MSW-002", name: "Marketing Warehouse" },
  { code: "MSW-003", name: "2nd Floor Warehouse" },
  { code: "MSW-004", name: "Motorpool(old) Warehouse" },
  { code: "MSW-005", name: "Asset Warehouse Caloocan" },
];

const DEFAULT_WHSE = "MSW-002";

function WarehouseDropdown({ value, onChange }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!value) onChange(DEFAULT_WHSE);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = WAREHOUSES.filter(w =>
    w.code.toLowerCase().includes(search.toLowerCase()) ||
    w.name.toLowerCase().includes(search.toLowerCase())
  );

  const selected = WAREHOUSES.find(w => w.code === value);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => setOpen(v => !v)}
        onKeyDown={e => e.stopPropagation()}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          border: "1.5px solid #e2e8f0", borderRadius: 7, padding: ".3rem .6rem",
          cursor: "pointer", background: "#fff", fontSize: ".82rem",
          color: selected ? "#1e293b" : "#94a3b8", minWidth: 130,
          gap: 4, userSelect: "none",
        }}
      >
        <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: ".78rem" }}>
          {selected ? selected.code : "Select..."}
        </span>
        <svg width={12} height={12} fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "110%", left: 0, zIndex: 9999,
          background: "#fff", border: "1.5px solid #e2e8f0",
          borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
          width: 280, maxHeight: 240, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              placeholder="Search warehouse..."
              style={{
                width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 7,
                padding: ".35rem .6rem", fontSize: ".82rem", outline: "none",
                fontFamily: "inherit", boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ overflowY: "auto", maxHeight: 180 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "1rem", color: "#94a3b8", fontSize: ".82rem", textAlign: "center" }}>
                No results
              </div>
            ) : filtered.map(w => (
              <div
                key={w.code}
                onClick={() => { onChange(w.code); setOpen(false); setSearch(""); }}
                style={{
                  padding: ".45rem .9rem",
                  cursor: "pointer",
                  background: value === w.code ? "#eff6ff" : "transparent",
                  borderLeft: value === w.code ? "3px solid #185FA5" : "3px solid transparent",
                  display: "flex", flexDirection: "column", gap: 1,
                }}
                onMouseEnter={e => { if (value !== w.code) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={e => { if (value !== w.code) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: ".78rem", color: "#185FA5" }}>
                  {w.code}
                </span>
                <span style={{ fontSize: ".75rem", color: "#64748b" }}>{w.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
const ACCOUNT_CODES = [
  { code: "101020700", label: "Due from Megasoft Cebu" },
  { code: "102020400", label: "Due from Cavite" },
  { code: "101020800", label: "Due from Solar Industries" },
  { code: "102020900", label: "Due from Batangas" },
  { code: "102020300", label: "Due from JKM CDO / Bukidnon" },
  { code: "102020500", label: "Due from Pampanga Branch" },
  { code: "102020600", label: "Due from Albay Branch" },
  { code: "102021000", label: "Due from Masbate Branch" },
  { code: "102022000", label: "Due from Ozamiz Branch" },
  { code: "101030215", label: "Receivable from Resigned Employees" },
  { code: "101030208", label: "Receivables from Agency Employees-Canteen" },
  { code: "1010304003", label: "Other Non-Trade Receivable-Others" },
  { code: "101030210", label: "Receivables from Agency Employees-Others" },
  { code: "0101040000", label: "Inventory" },
  { code: "101050701", label: "Office Supplies" },
  { code: "101050705", label: "General Supplies" },
  { code: "101050702", label: "Factory Supplies" },
  { code: "101050703", label: "Mechanical Electrical Supplies" },
  { code: "101050704", label: "Motorpool Supplies" },
  { code: "101050500", label: "Marketing Materials Supplies" },
  { code: "0101070000", label: "Property Plant & Equipment" },
  { code: "102010101", label: "Land Improvements" },
  { code: "102010200", label: "Building" },
  { code: "102011600", label: "Machinery Tools & Equipment" },
  { code: "102011800", label: "Furnitures, Fixtures & Equipment" },
  { code: "102010600", label: "Computer Equipment & Software" },
  { code: "102012930", label: "CIP - MegaOne" },
  { code: "102010800", label: "Delivery Vehicles" },
  { code: "102011000", label: "Transportation Equipment" },
  { code: "102012910", label: "CIP-CDO" },
  { code: "102012620", label: "CIP-Cebu" },
  { code: "102012700", label: "Land & Building Mega Soft" },
  { code: "0101070100", label: "Advances to Suppliers" },
  { code: "0101070500", label: "Creditable Withholding Tax" },
  { code: "0101070200", label: "Input VAT" },
  { code: "106011010", label: "CIP-Materials-Balagtas" },
  { code: "101071000", label: "Advances to Related Party (ML)" },
];

const DEFAULT_ACCOUNT = "101050500";

function AccountCodeDropdown({ value, onChange }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  // Set default on mount
  React.useEffect(() => {
    if (!value) onChange(DEFAULT_ACCOUNT);
  }, []);

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = ACCOUNT_CODES.filter(a =>
    a.code.includes(search) ||
    a.label.toLowerCase().includes(search.toLowerCase())
  );

  const selected = ACCOUNT_CODES.find(a => a.code === value);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          border: "1.5px solid #e2e8f0", borderRadius: 7, padding: ".3rem .6rem",
          cursor: "pointer", background: "#fff", fontSize: ".82rem",
          color: selected ? "#1e293b" : "#94a3b8", minWidth: 130,
          gap: 4, userSelect: "none",
        }}
      >
        <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: ".78rem" }}>
          {selected ? `${selected.code}` : "Select..."}
        </span>
        <svg width={12} height={12} fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (
        <div style={{
          position: "absolute", top: "110%", left: 0, zIndex: 9999,
          background: "#fff", border: "1.5px solid #e2e8f0",
          borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.13)",
          width: 320, maxHeight: 260, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          {/* Search */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.stopPropagation()}
              onClick={e => e.stopPropagation()}
              placeholder="Search code or name..."
              style={{
                width: "100%", border: "1.5px solid #e2e8f0", borderRadius: 7,
                padding: ".35rem .6rem", fontSize: ".82rem", outline: "none",
                fontFamily: "inherit", boxSizing: "border-box",
              }}
            />
          </div>

          {/* List */}
          <div style={{ overflowY: "auto", maxHeight: 200 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "1rem", color: "#94a3b8", fontSize: ".82rem", textAlign: "center" }}>
                No results
              </div>
            ) : filtered.map(a => (
              <div
                key={a.code}
                onClick={() => { onChange(a.code); setOpen(false); setSearch(""); }}
                style={{
                  padding: ".45rem .9rem",
                  cursor: "pointer",
                  background: value === a.code ? "#eff6ff" : "transparent",
                  borderLeft: value === a.code ? "3px solid #185FA5" : "3px solid transparent",
                  display: "flex", flexDirection: "column", gap: 1,
                }}
                onMouseEnter={e => { if (value !== a.code) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={e => { if (value !== a.code) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: ".78rem", color: "#185FA5" }}>
                  {a.code}
                </span>
                <span style={{ fontSize: ".75rem", color: "#64748b" }}>{a.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ─── Main Form ────────────────────────────────────────────────────────────────
export default function GR_Marketing_FORM() {
  const [form, setForm] = useState(getInitialForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [activeTab, setActiveTab] = useState("contents");

  // All items fetched once
  const [allItems, setAllItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Fetch items once on mount
  useEffect(() => {
    const fetchItems = async () => {
      setItemsLoading(true);
      try {
        const res = await fetch(ITEMS_API);
        if (res.ok) {
          const data = await res.json();
          setAllItems(data.filter(i => i.isActive === 1));
        }
      } catch (err) { console.error(err); }
      setItemsLoading(false);
    };
    fetchItems();
  }, []);

  const handleField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm((f) => ({ ...f, items }));
  };

  // When user selects from typeahead — fill both itemNo and itemDescription
  const handleSelectItem = (idx, selectedItem) => {
    const items = [...form.items];
    items[idx] = {
      ...items[idx],
      itemNo: selectedItem.itemCode || "",
      itemDescription: selectedItem.description || "",
    };
    setForm((f) => ({ ...f, items }));
  };

  // Modal state
  const [lookupOpen, setLookupOpen] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);

  const openModal = (idx) => { setActiveRowIdx(idx); setLookupOpen(true); };
  const handleModalSelect = (selectedItem) => {
    if (activeRowIdx === null) return;
    handleSelectItem(activeRowIdx, selectedItem);
    setLookupOpen(false);
    setActiveRowIdx(null);
  };

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [...f.items, { itemNo: "", itemDescription: "", quantity: "", accountCode: "", unitPrice: "", whse: "", expenseCategory: "", addtlInfo: "" }],
    }));

  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  // ── Submit ──
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        number: form.number,
        series: form.series,
        priceList: form.priceList || null,
        postingDate: form.postingDate,
        documentDate: form.documentDate,
        ref2: form.ref2 || null,
        remarks: form.remarks || null,
        journalRemark: form.journalRemark || "Goods Receipt",
        items: form.items.map((item) => ({
          itemNo: item.itemNo,
          itemDescription: item.itemDescription || null,
          quantity: Number(item.quantity) || 0,
          accountCode: item.accountCode || null,
          unitPrice: Number(item.unitPrice) || 0,
          whse: item.whse || null,
          expenseCategory: item.expenseCategory || null,
          addtlInfo: item.addtlInfo || null,
        })),
      };

      const res = await fetch(`${API_BASE_URL}api/GR_Marketing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const details = err?.errors
          ? Object.entries(err.errors).map(([k, v]) => `${k}: ${v}`).join(" | ")
          : null;
        throw new Error(details || err?.message || `Server error ${res.status}`);
      }

      const result = await res.json();
      setSavedData(result);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(getInitialForm());
    setSubmitted(false);
    setSavedData(null);
    setError(null);
  };

  // ── SUCCESS SCREEN ────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: '"Outfit", sans-serif' }}>
        <style>{`@keyframes gr-spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
        <div style={{ background: "#fff", borderRadius: "20px", padding: "3rem", textAlign: "center", maxWidth: "420px", width: "100%", boxShadow: "0 8px 32px rgba(0,0,0,.1)", border: "1px solid #e2e8f0" }}>
          <div style={{ width: 64, height: 64, background: "#d1fae5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem" }}>
            <svg width={32} height={32} fill="none" stroke="#059669" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#0f172a", margin: "0 0 .5rem" }}>Goods Receipt Saved!</h2>
          <p style={{ color: "#64748b", margin: "0 0 .25rem", fontSize: ".9rem" }}>
            Number: <span style={{ fontWeight: 700, color: "#185FA5" }}>{savedData?.number || form.number}</span>
          </p>
          <p style={{ color: "#94a3b8", fontSize: ".82rem", margin: "0 0 1.5rem" }}>
            ID: <span style={{ fontFamily: "monospace", color: "#475569" }}>#{savedData?.id}</span>
          </p>
          <p style={{ color: "#94a3b8", fontSize: ".78rem", margin: "0 0 1.5rem", background: "#f8fafc", borderRadius: 8, padding: ".5rem .75rem", border: "1px solid #e2e8f0" }}>
            📦 Go to the <strong style={{ color: "#185FA5" }}>GR List</strong> and click <strong style={{ color: "#1D9E75" }}>Post</strong> to update stock.
          </p>
          <button onClick={handleReset} style={{ background: "#185FA5", color: "#fff", border: "none", borderRadius: 10, padding: ".7rem 1.75rem", fontWeight: 700, fontSize: ".9rem", cursor: "pointer", fontFamily: "inherit" }}>
            New Form
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN FORM ─────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');
        @keyframes gr-spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes gr-fadein { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        .gr-row:hover td { background: #f0f7ff !important; }
        .gr-row td { transition: background .1s; }
        .gr-input { width:100%; border:1px solid #e2e8f0; border-radius:7px; padding:.4rem .6rem; font-size:.83rem; outline:none; font-family:"Outfit",sans-serif; background:#fff; color:#1e293b; box-sizing:border-box; transition:border-color .15s,box-shadow .15s; }
        .gr-input:focus { border-color:#185FA5; box-shadow:0 0 0 3px rgba(24,95,165,.1); }
        .gr-input::placeholder { color:#cbd5e1; }
        .gr-input[readonly] { background:#f8fafc; color:#94a3b8; cursor:not-allowed; }
      `}</style>

      <ItemLookupModal
        open={lookupOpen}
        onClose={() => { setLookupOpen(false); setActiveRowIdx(null); }}
        onSelect={handleModalSelect}
        allItems={allItems}
        loading={itemsLoading}
      />

      <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: '"Outfit", sans-serif', padding: "2rem 1.5rem" }}>
        <div style={{ maxWidth: 1600, margin: "0 auto" }}>
          <div style={{ background: "#fff", borderRadius: "18px", border: "1px solid #e2e8f0", overflow: "visible", boxShadow: "0 2px 12px rgba(0,0,0,.07)", animation: "gr-fadein .3s ease" }}>

            {/* Title Bar */}
            <div style={{ background: "linear-gradient(135deg,#185FA5,#0C447C)", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", borderRadius: "18px 18px 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                <svg width={22} height={22} fill="none" stroke="#fff" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h1 style={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem", margin: 0 }}>Goods Receipt</h1>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                {itemsLoading && (
                  <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#bfdbfe", fontSize: ".72rem" }}>
                    <svg style={{ animation: "gr-spin .7s linear infinite", width: 12, height: 12 }} fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .3 }} />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .8 }} />
                    </svg>
                    Loading items…
                  </span>
                )}
                <span style={{ background: "rgba(255,255,255,.18)", color: "#bfdbfe", fontSize: ".75rem", fontWeight: 600, padding: ".2rem .75rem", borderRadius: 99, border: "1px solid rgba(255,255,255,.2)" }}>
                  Marketing
                </span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ margin: "1rem 1.5rem 0", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: ".75rem 1rem", borderRadius: 10, fontSize: ".875rem", display: "flex", alignItems: "center", gap: 8 }}>
                <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
                <button onClick={() => setError(null)} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#b91c1c", fontSize: "1rem" }}>✕</button>
              </div>
            )}

            {/* Top Fields */}
            <div style={{ padding: "1.25rem 1.5rem" }}>
              <div style={{ display: "flex", gap: "3rem", flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: ".75rem", flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <label style={{ fontSize: ".72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", minWidth: 70 }}>Number</label>
                    <input value={form.number} readOnly className="gr-input" style={{ width: 160, fontFamily: "monospace", background: "#f8fafc", color: "#94a3b8" }} />
                    <label style={{ fontSize: ".72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>Series</label>
                    <select value={form.series} onChange={e => handleField("series", e.target.value)}
                      style={{ padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".875rem", outline: "none", fontFamily: "inherit", color: "#1e293b", width: 130 }}>
                      <option>Primary</option>
                      <option>Secondary</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                    <label style={{ fontSize: ".72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", minWidth: 70 }}>Price List</label>
                    <select value={form.priceList} onChange={e => handleField("priceList", e.target.value)}
                      style={{ padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".875rem", outline: "none", fontFamily: "inherit", color: "#1e293b", width: 220 }}>
                      <option>Last Evaluated Price</option>
                      <option>Standard Price</option>
                      <option>Sales Price</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ".625rem", minWidth: 230 }}>
                  {[
                    { label: "Posting Date", field: "postingDate", type: "date" },
                    { label: "Document Date", field: "documentDate", type: "date" },
                    { label: "Ref. 2", field: "ref2", placeholder: "—" },
                  ].map(({ label, field, type, placeholder }) => (
                    <div key={field} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                      <label style={{ fontSize: ".72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap" }}>{label}</label>
                      <input type={type || "text"} value={form[field]} onChange={e => handleField(field, e.target.value)}
                        placeholder={placeholder}
                        style={{ padding: ".4rem .75rem", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: ".875rem", outline: "none", fontFamily: "inherit", width: 140 }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #e2e8f0", display: "flex" }}>
              {["contents", "attachments"].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} style={{
                  padding: ".75rem 1.5rem", border: "none", borderBottom: `2px solid ${activeTab === tab ? "#185FA5" : "transparent"}`,
                  background: activeTab === tab ? "#eff6ff" : "transparent",
                  color: activeTab === tab ? "#185FA5" : "#64748b",
                  fontWeight: 600, fontSize: ".875rem", cursor: "pointer", textTransform: "capitalize", fontFamily: "inherit", transition: "all .15s",
                }}>
                  {tab}
                </button>
              ))}
            </div>

            {/* Contents Tab */}
            {activeTab === "contents" && (
              <div style={{ overflowX: "auto", overflowY: "visible" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                      {[
                        { label: "#", w: 44 },
                        { label: "(01) Item No.", w: 160 },
                        { label: "(02) Item Description", w: 240 },
                        { label: "(03) Quantity", w: 110 },
                        { label: "(06) Account Code", w: 140 },
                        { label: "Unit Price", w: 110 },
                        { label: "(04) Whse", w: 110 },
                        { label: "(05) Expense Category", w: 150 },
                        { label: "Addtl Info", w: 140 },
                        { label: "", w: 40 },
                      ].map(({ label, w }) => (
                        <th key={label} style={{ padding: ".75rem .875rem", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: ".7rem", textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap", width: w }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, idx) => (
                      <tr key={idx} className="gr-row" style={{ position: "relative" }}>
                        <td style={{ padding: ".5rem .875rem", color: "#cbd5e1", fontFamily: "monospace", fontSize: ".78rem", borderBottom: "1px solid #f8fafc" }}>
                          {idx + 1}
                        </td>

                        {/* Item No. — typeahead */}
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc", overflow: "visible", position: "relative", zIndex: 10 }}>
                          <TypeaheadInput
                            value={item.itemNo}
                            onChange={val => handleItem(idx, "itemNo", val)}
                            onSelect={selected => handleSelectItem(idx, selected)}
                            onOpenModal={() => openModal(idx)}
                            allItems={allItems}
                            field="itemNo"
                            placeholder="e.g. MK0013"
                          />
                        </td>

                        {/* Item Description — typeahead */}
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc", overflow: "visible", position: "relative", zIndex: 9 }}>
                          <TypeaheadInput
                            value={item.itemDescription}
                            onChange={val => handleItem(idx, "itemDescription", val)}
                            onSelect={selected => handleSelectItem(idx, selected)}
                            onOpenModal={() => openModal(idx)}
                            allItems={allItems}
                            field="itemDescription"
                            placeholder="FINISHED GOODS"
                          />
                        </td>

                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc" }}>
                          <input type="number" value={item.quantity} onChange={e => handleItem(idx, "quantity", e.target.value)} placeholder="0" className="gr-input" />
                        </td>
                        <td
                          style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc", position: "relative", zIndex: 8 }}
                          onKeyDown={e => e.stopPropagation()}
                        >
                          <AccountCodeDropdown
                            value={item.accountCode}
                            onChange={val => handleItem(idx, "accountCode", val)}
                          />
                        </td>
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc" }}>
                          <input type="number" value={item.unitPrice} onChange={e => handleItem(idx, "unitPrice", e.target.value)} placeholder="0.00" className="gr-input" />
                        </td>
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc", position: "relative", zIndex: 7 }}
                          onKeyDown={e => e.stopPropagation()}>
                          <WarehouseDropdown
                            value={item.whse}
                            onChange={val => handleItem(idx, "whse", val)}
                          />
                        </td>
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc" }}>
                          <input value={item.expenseCategory} onChange={e => handleItem(idx, "expenseCategory", e.target.value)} placeholder="—" className="gr-input" />
                        </td>
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc" }}>
                          <input value={item.addtlInfo} onChange={e => handleItem(idx, "addtlInfo", e.target.value)} placeholder="—" className="gr-input" />
                        </td>
                        <td style={{ padding: ".375rem .5rem", borderBottom: "1px solid #f8fafc" }}>
                          {form.items.length > 1 && (
                            <button onClick={() => removeItem(idx)}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", display: "flex", padding: ".2rem", borderRadius: 5 }}
                              onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                              onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>
                              <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {Array.from({ length: Math.max(0, 5 - form.items.length) }).map((_, i) => (
                      <tr key={`empty-${i}`} style={{ height: 38, borderBottom: "1px solid #f8fafc" }}>
                        {Array.from({ length: 10 }).map((_, j) => <td key={j} style={{ padding: ".5rem .875rem" }}></td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding: ".75rem 1rem", borderTop: "1px solid #f1f5f9" }}>
                  <button onClick={addItem}
                    style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: "#185FA5", fontWeight: 600, fontSize: ".875rem", fontFamily: "inherit", padding: ".3rem .5rem", borderRadius: 7 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    <svg width={15} height={15} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Row
                  </button>
                </div>
              </div>
            )}

            {/* Attachments Tab */}
            {activeTab === "attachments" && (
              <div style={{ padding: "3rem", textAlign: "center", color: "#94a3b8" }}>
                <svg width={40} height={40} fill="none" stroke="#cbd5e1" viewBox="0 0 24 24" style={{ margin: "0 auto 8px" }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span style={{ fontSize: ".9rem" }}>No attachments yet.</span>
              </div>
            )}

            {/* Remarks + Journal */}
            <div style={{ borderTop: "1px solid #e2e8f0", padding: "1.25rem 1.5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
              <div>
                <label style={{ display: "block", fontSize: ".72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".5rem" }}>Remarks</label>
                <textarea value={form.remarks} onChange={e => handleField("remarks", e.target.value)} rows={2} placeholder="Enter remarks..."
                  style={{ width: "100%", padding: ".625rem .875rem", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: ".875rem", outline: "none", fontFamily: "inherit", resize: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: ".72rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: ".5rem" }}>Journal Remark</label>
                <input value={form.journalRemark} onChange={e => handleField("journalRemark", e.target.value)}
                  style={{ width: "100%", padding: ".625rem .875rem", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: ".875rem", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ borderTop: "1px solid #e2e8f0", padding: "1rem 1.5rem", display: "flex", alignItems: "center", gap: ".75rem" }}>
              <button onClick={handleSubmit} disabled={loading}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: loading ? "#94a3b8" : "linear-gradient(135deg,#f59e0b,#d97706)",
                  color: "#fff", border: "none", borderRadius: 9,
                  padding: ".625rem 1.5rem", fontWeight: 700, fontSize: ".875rem",
                  cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
                  boxShadow: loading ? "none" : "0 2px 8px rgba(245,158,11,.35)",
                }}>
                {loading ? (
                  <>
                    <svg style={{ animation: "gr-spin .7s linear infinite", width: 15, height: 15 }} fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: .25 }} />
                      <path fill="currentColor" d="M4 12a8 8 0 018-8v8z" style={{ opacity: .75 }} />
                    </svg>
                    Saving…
                  </>
                ) : "Add"}
              </button>
              <button onClick={handleReset}
                style={{ padding: ".625rem 1.25rem", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, fontWeight: 600, fontSize: ".875rem", cursor: "pointer", color: "#475569", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}